import { BasicButton, CustomInput, Loading, Typography } from "@/components";
import env from "@/env.config";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import { AuthLoginSchema, AuthWebviewSchema } from "@/schemas";
import { memberService } from "@/services";
import type { AuthSignInResponse, ResError } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Box from "@mui/material/Box";
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import {
	Link,
	createFileRoute,
	redirect,
	useRouter,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { omit } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import OneSignal from "react-onesignal";
import { toast } from "sonner";

export const Route = createFileRoute("/")({
	validateSearch: zodSearchValidator(AuthWebviewSchema),
	beforeLoad: async () => {
		const isAuthed = isAuthenticating();
		if (isAuthed) {
			throw redirect({
				to: "/dashboard",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const isMounted = useRef<boolean>(false);
	const router = useRouter();
	const navigate = Route.useNavigate();
	const message = Route.useSearch({
		select: (s) => s.message,
	});
	const [showPassword, setShowPassword] = useState(false);

	const { mutateAsync: onMutateLogin, isError } = useMutation<
		AuthSignInResponse,
		ResError,
		AuthLoginSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_AUTH_LOGIN],
	});

	const {
		control,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<AuthLoginSchema>({
		mode: "all",
		resolver: zodResolver(AuthLoginSchema),
	});

	const onSubmit: SubmitHandler<AuthLoginSchema> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return onMutateLogin(data, {
			onSuccess: async (res) => {
				if (env.isSecureHttp) {
					OneSignal.login(res.user.id.toString());
				}
				memberService.updateVersion({
					app_version: env.appVersion,
					platform: "web",
				});
				Sentry.isEnabled() &&
					Sentry.setUser({
						id: res.user.id,
						email: res.user.email,
						...(res.user.fullname ? { username: res.user.fullname } : {}),
					});
				await navigate({
					to: "/dashboard",
				});
				return router.invalidate();
			},
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (message && !isMounted.current) {
			toast.error(message, {
				position: "bottom-center",
			});
			navigate({
				replace: true,
				// @ts-expect-error
				search: (old) => omit(old, ["message"]),
			});
			isMounted.current = true;
		}
	}, [message]);

	return (
		<>
			<div className="container h-full bg-gradient mx-auto overflow-hidden">
				<div className="max-w-wrap mx-auto h-full pt-8 md:pt-14 pb-4 flex flex-col justify-between items-center">
					<Box
						component="form"
						onSubmit={handleSubmit(onSubmit)}
						className="w-full flex flex-col justify-center items-center gap-8 md:gap-10"
					>
						<img src="/images/new-logo-wh.png" alt="logo" loading="lazy" style={{width: "210px"}} />
						{isError && (
							<Box className="bg-danger-100 w-full flex items-start border border-danger-300 rounded-xl gap-2 p-4">
								<ErrorRoundedIcon className="text-danger-300 !text-base" />
								<div>
									<p className="text-xs !leading-4 text-dark">
										The email address or password is incorrect. Please try
										again.
									</p>
								</div>
							</Box>
						)}
						<div className="w-full flex flex-col gap-1 md:gap-2">
							<Controller
								control={control}
								name="email"
								defaultValue=""
								render={({ field, fieldState }) => (
									<CustomInput
										className="input-field"
										type="email"
										label="Email"
										name="email"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										errorMessage={fieldState.error?.message}
										startIcon={<MailOutlineIcon />}
									/>
								)}
							/>
							<Controller
								control={control}
								name="password"
								defaultValue=""
								render={({ field, fieldState }) => (
									<CustomInput
										className="input-field"
										type="password"
										label="Password"
										name="password"
										autoComplete="current-password"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										onToggleVisibility={() => setShowPassword((show) => !show)}
										showPassword={showPassword}
										errorMessage={fieldState.error?.message}
										startIcon={<LockOutlinedIcon />}
									/>
								)}
							/>
						</div>

						<div className="w-full flex flex-col justify-center items-center gap-2">
							<BasicButton
								className="submit w-full !h-14 !text-base !shadow-none"
								variant="contained"
								color="primary"
								disable={!isValid || !isDirty || isSubmitting}
								type="submit"
							>
								SIGN IN
							</BasicButton>
							<Link to="/forgot-password" preload={false}>
								<h1 className="text-secondary text-sm font-semibold py-3.5">
									FORGOT PASSWORD
								</h1>
							</Link>
						</div>
					</Box>
					<div className="w-full flex flex-col justify-center items-center gap-4">
						<Link className="w-full" to="/sso/signin" preload={false}>
							<BasicButton
								className="w-full !h-14 !text-base"
								variant="outlined"
								color="primary"
							>
								Sign in Using SSO
							</BasicButton>
						</Link>
						<Link className="w-full" to="/create-account" preload={false}>
							<BasicButton
								className="w-full !h-14 !text-base"
								variant="outlined"
								color="primary"
							>
								CREATE ACCOUNT
							</BasicButton>
						</Link>
						<Typography fontWeight={400} fontSize={10} className="text-white">
							HEMI Teams&nbsp;
							<Link to="/term-service" className="hover:underline">
								<Typography component="span" fontWeight={700} fontSize={10}>
									Terms of Service,&nbsp;
								</Typography>
							</Link>
							<Link to="/privacy-policy" className="hover:underline">
								<Typography component="span" fontWeight={700} fontSize={10}>
									Privacy Policy,&nbsp;
								</Typography>
							</Link>
							<Link to="/cookie-policy" className="hover:underline">
								<Typography component="span" fontWeight={700} fontSize={10}>
									Cookie Policy
								</Typography>
							</Link>
						</Typography>
					</div>
				</div>
			</div>
			<Loading status={isSubmitting} />
		</>
	);
}
